/**
 * PhysicsSystem — initializes Havok physics and provides helpers for
 * creating physics-enabled ground and platforms.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import "@babylonjs/core/Physics/joinedPhysicsEngineComponent";
import { HavokPlugin } from "@babylonjs/core/Physics/v2/Plugins/havokPlugin";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { PhysicsMotionType, PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import HavokPhysics from "@babylonjs/havok";
import { PHYSICS, COLORS } from "../config/GameConfig";
import { tagAs } from "../levels/PlaceholderTag";

/** Cache the Havok WASM instance so it's only loaded once across scene transitions. */
let cachedHavokInstance: Awaited<ReturnType<typeof HavokPhysics>> | null = null;

export class PhysicsSystem {
  private scene: Scene;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /** Initialize the Havok physics plugin on the scene. Must be awaited. */
  async init(): Promise<void> {
    if (!cachedHavokInstance) {
      cachedHavokInstance = await HavokPhysics({
        locateFile: () => "/HavokPhysics.wasm",
      });

      // Cap Havok's internal substep at 1/60s. Babylon's HavokPlugin
      // sets the world's "ideal step time" to the WHOLE frame delta on
      // every step, which disables Havok's built-in substepping. On a
      // machine rendering below 60 fps the engine's clamped delta is up
      // to 33ms — an Overclocked-Leap descent then moves ~0.6u per step,
      // more than the cat capsule's 0.35u radius, and the capsule
      // tunnels straight through 0.4u-thick platform decks (observed:
      // Basil falling through plat_s1_4 on every landing at ~5 fps).
      // With the ideal step capped, HP_World_Step subdivides a 33ms
      // frame into 60Hz substeps internally and landings behave
      // identically to a healthy machine. No-op at ≥60 fps.
      const raw = cachedHavokInstance.HP_World_SetIdealStepTime.bind(
        cachedHavokInstance,
      );
      cachedHavokInstance.HP_World_SetIdealStepTime = (world, dt) =>
        raw(world, Math.min(dt, 1 / 60));
    }
    const havokPlugin = new HavokPlugin(true, cachedHavokInstance);
    this.scene.enablePhysics(
      new Vector3(0, PHYSICS.GRAVITY_Y, 0),
      havokPlugin
    );
  }

  /** Create a ground plane with a static physics body. */
  createGround(width: number, depth: number, centerX = 0): Mesh {
    const ground = MeshBuilder.CreateBox(
      "ground",
      { width, height: 0.5, depth },
      this.scene
    );
    ground.position.x = centerX;
    ground.position.y = -0.25; // Top surface at y=0

    const mat = new StandardMaterial("groundMat", this.scene);
    mat.diffuseColor = Color3.FromHexString(COLORS.GROUND);
    mat.specularColor = Color3.Black();
    ground.material = mat;

    // Static body (mass 0)
    new PhysicsAggregate(
      ground,
      PhysicsShapeType.BOX,
      { mass: 0, friction: 0.8, restitution: 0.1 },
      this.scene
    );

    // Ground is an invisible physics-only surface — the rendered ground
    // comes from the level's environment art. Tag accordingly so audits
    // distinguish it from authored meshes.
    tagAs(ground, "placeholder");

    return ground;
  }

  /** Create a platform box at a given position with a static physics body. */
  createPlatform(
    name: string,
    width: number,
    height: number,
    depth: number,
    position: Vector3
  ): Mesh {
    const platform = MeshBuilder.CreateBox(
      name,
      { width, height, depth },
      this.scene
    );
    platform.position = position;

    const mat = new StandardMaterial(`${name}Mat`, this.scene);
    mat.diffuseColor = Color3.FromHexString(COLORS.PLATFORM);
    mat.specularColor = Color3.Black();
    platform.material = mat;

    new PhysicsAggregate(
      platform,
      PhysicsShapeType.BOX,
      { mass: 0, friction: 0.8, restitution: 0.1 },
      this.scene
    );

    // Platforms are invisible physics-only surfaces — the level renders
    // them via walkable cue planks / brass trim. Tag as placeholder so
    // the ledger stays consistent.
    tagAs(platform, "placeholder");

    this.platformMeshes.push(platform);
    return platform;
  }

  /**
   * Put an invisible, frictionless wall just past each horizontal end of
   * the level (the X extent of every static collider), so walking the
   * "wrong" way at a level's start or end can't drop a cat into the void.
   * Playtest found L1's opening prompt sent both cats off the start edge
   * (4 respawns in 3 s). Call once after the level's colliders exist. The
   * walls are NOT added to the platform registry (they aren't walkable).
   */
  sealLevelEnds(): void {
    let minX = Infinity;
    let maxX = -Infinity;
    for (const mesh of this.scene.meshes) {
      const body = mesh.physicsBody;
      if (!body || mesh.name.startsWith("levelEndWall")) continue;
      if (body.getMotionType() !== PhysicsMotionType.STATIC) continue;
      mesh.computeWorldMatrix(true);
      const bb = mesh.getBoundingInfo().boundingBox;
      minX = Math.min(minX, bb.minimumWorld.x);
      maxX = Math.max(maxX, bb.maximumWorld.x);
    }
    if (!Number.isFinite(minX) || !Number.isFinite(maxX)) return;
    for (const [name, x] of [["levelEndWallMin", minX - 0.5], ["levelEndWallMax", maxX + 0.5]] as const) {
      const wall = MeshBuilder.CreateBox(name, { width: 1, height: 80, depth: 6 }, this.scene);
      wall.position.set(x, 20, 0);
      wall.isVisible = false;
      wall.isPickable = false;
      new PhysicsAggregate(wall, PhysicsShapeType.BOX, { mass: 0, friction: 0, restitution: 0 }, this.scene);
    }
  }

  /**
   * Meshes created via `createPlatform` — the authoritative list of
   * physics-backed walkable surfaces for this level. Consumed by the
   * QA navigator's platform registry (`scene.metadata.level
   * .getPlatforms()`) so goTo reasons about real colliders rather than
   * regex-matching every mesh whose name contains "plat" (which sweeps
   * in decoration meshes like `platform1_support_beam` and causes
   * false ceiling detection).
   */
  private platformMeshes: Mesh[] = [];

  /**
   * Register an externally-created walkable mesh in the platform
   * registry. Use for GLB-imported platforms / scene geometry that
   * got its physics body attached outside of `createPlatform`.
   * Idempotent — repeated registration of the same mesh is a no-op.
   *
   * The mesh's world-space AABB is snapshotted on every registry
   * query, so the caller doesn't need to worry about keeping the
   * snapshot fresh — the navigator reads it live via
   * `getPlatformRegistry()`.
   */
  registerImportedPlatform(mesh: Mesh): void {
    if (!mesh) return;
    if (this.platformMeshes.includes(mesh)) return;
    this.platformMeshes.push(mesh);
  }

  /**
   * Snapshot of every platform created by this PhysicsSystem, with
   * world-space AABB computed from the current mesh transform. Called
   * by the level handle's `getPlatforms()`.
   */
  getPlatformRegistry(): Array<{
    name: string;
    minX: number; maxX: number;
    minY: number; maxY: number;
    minZ: number; maxZ: number;
  }> {
    const out: Array<{
      name: string;
      minX: number; maxX: number;
      minY: number; maxY: number;
      minZ: number; maxZ: number;
    }> = [];
    for (const mesh of this.platformMeshes) {
      if (mesh.isDisposed()) continue;
      mesh.computeWorldMatrix(true);
      const bb = mesh.getBoundingInfo().boundingBox;
      out.push({
        name: mesh.name,
        minX: bb.minimumWorld.x,
        maxX: bb.maximumWorld.x,
        minY: bb.minimumWorld.y,
        maxY: bb.maximumWorld.y,
        minZ: bb.minimumWorld.z,
        maxZ: bb.maximumWorld.z,
      });
    }
    return out;
  }
}
